import type { CancelToken } from 'axios';

import { RecommendationsApiFp as recommendationsApi } from './api/recommendations-api';
import {
  type ApiConfig,
  configure,
  hirerExpAxiosInstance,
  invoke,
} from './apiInstance';
import { DefaultDelay, retrier } from './apiRetry';
import type { JobDetails } from './models/job-details';
import type { Recommendations } from './models/recommendations';
import type { SelectedQuestion } from './models/selected-question';

const api = recommendationsApi();

export interface CreateRecommendationOptions {
  id: string;
  limit?: number;
  jobDetails: PublicJobDetails;
  selectedQuestions?: SelectedQuestion[];
  country: string;
  versionNumber: string;
}

export type PublicJobDetails = Omit<JobDetails, 'websiteCountryId'>;

export interface VersionedRecommendations extends Recommendations {
  versionNumber: string;
}

export async function createRecommendations(
  {
    id,
    jobDetails,
    limit = 10,
    selectedQuestions = [],
    country,
    versionNumber,
  }: CreateRecommendationOptions,
  cancelToken: CancelToken,
): Promise<VersionedRecommendations> {
  const recommendationResponse = await retrier(
    () =>
      hirerExpAxiosInstance().post(
        `/questionnaires/${id}/recommendations`,
        {
          jobDetails: { ...jobDetails, websiteCountryId: country },
          questionLimit: limit,
          selectedQuestions,
        },
        {
          headers: { 'If-Match': versionNumber },
          cancelToken,
        },
      ),
    {
      strategy: 'fixed',
      delayMs: DefaultDelay,
    },
  );

  return {
    versionNumber: recommendationResponse.headers.etag,
    ...recommendationResponse.data,
  };
}

export async function getRecommendations(
  questionnaireId: string,
  config: ApiConfig,
  cancelToken?: CancelToken,
): Promise<VersionedRecommendations> {
  configure(config);

  const recommendationResponse = await retrier(
    () =>
      hirerExpAxiosInstance().get(
        `/questionnaires/${questionnaireId}/recommendations`,
        {
          cancelToken,
        },
      ),
    {
      strategy: 'fixed',
      delayMs: DefaultDelay,
    },
  );

  return {
    versionNumber: recommendationResponse.headers.etag,
    ...recommendationResponse.data,
  };
}

type ETag = string;

interface SaveDraftSelectedQuestionsOptions {
  questionnaireId: string;
  versionNumber: string;
  isNewHirerExperienceAPIEnabled: boolean;
  cancelToken: CancelToken;
  selectedQuestions: SelectedQuestion[];
}

export async function saveDraftSelectedQuestions({
  questionnaireId,
  versionNumber,
  isNewHirerExperienceAPIEnabled,
  cancelToken,
  ...request
}: SaveDraftSelectedQuestionsOptions): Promise<ETag> {
  if (isNewHirerExperienceAPIEnabled) {
    const recommendationResponse = await retrier(
      () =>
        hirerExpAxiosInstance().put(
          `/questionnaires/${questionnaireId}/recommendations`,
          request,
          {
            headers: { 'If-Match': versionNumber },
            cancelToken,
          },
        ),
      {
        strategy: 'fixed',
        delayMs: DefaultDelay,
      },
    );

    return recommendationResponse.headers.etag;
  }
  const recommendationResponse = await invoke(
    api.recommendationsPut(questionnaireId, request, versionNumber),
  );
  return recommendationResponse.headers.etag;
}
